import { AddressTextField } from '@components/AddressTextField/AddressTextField'
import BottomSheet from '@components/BottomSheet'
import Button from '@components/Button'
import { ContactList } from '@components/ContactList/ContactList'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import { TokenList } from '@components/TokenList/TokenList'
import { TokenListItem } from '@components/TokenList/TokenListItem'
import { sanitizeDecimalInput } from '@plug-core/utils/validationHelper'
import {
  ListItem,
  Stack,
  Typography,
  Paper,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import SendReview from './Components/SendReview'
import { NftList } from '@components/NftList/NftList'
import NFTDisplayer from '@components/NftDisplayer'
import ErrorPopup from '@components/ErrorPopup'
import Picker from '@components/Picker'
import Touchable from '@components/Touchable'
import { formatNumber } from '@plug-core/utils/formatNumber'
import useSend, { SendMode } from '@plug-core/hooks/useSend'
import { getPrincipals } from '@plug-core/handlers/walletActions/wallet'
import { logger } from '@plug-core/logger'
import { FaArrowDown, FaArrowUp } from 'react-icons/fa6'
import { useLocation, useNavigate } from 'react-router-dom'
import { Titlebar } from '@components/Titlebar/Titlebar'

const Send = () => {
  const [walletPrincipals, setWalletPrincipals] = useState<string[]>([])
  const { t } = useTranslation()

  const { state } = useLocation()

  const mode = state?.topup ? SendMode.CycleTopup : SendMode.Send

  const navigate = useNavigate()

  const {
    addressMatched,
    hasSelectedAsset,
    toAddress,
    setToAddress,
    addressType,
    nft,
    nfts,
    token,
    balancesForSelect,
    onTokenSelect,
    isFindingTokenBalances,
    setIsFindingTokenBalances,
    findTokens,
    setNft,
    amount,
    setAmount,
    maxAmount,
    setSelectedMode,
    amountCurrency,
    feeAmountCurrency,
    setAmountCurrency,
    userLocale,
    xtcSendMode,
    setXtcSendMode,
    fee,
    validateAndShowSummary,
    error,
    summaryVisible,
    setSummaryVisible,
    e8sAmount,
    utxosToUse,
    sendError,
    setSendError,
    wallet,
    onError,
    walletNfts,
    canSendNft,
    showXTCSendMode,
    cyclesOutAmount,
    contactSections,
    receiverChain,
    maxFeePerGas,
    maxPriorityFeePerGas,
    erc20TransferGas,
    gasLimit,
  } = useSend({
    walletPrincipals,
    selectedTokenId: state?.selectedTokenId,
    selectedNft: state?.selectedNft,
    mode,
  })

  useEffect(() => {
    getPrincipals()
      .then((principals) => setWalletPrincipals(principals.map((p) => p.toString())))
      .catch(logger.error)
  }, [])

  const ordinals = walletNfts.flatMap((collection) =>
    collection.nfts.filter((nft) => nft.standard === 'ordinal')
  )

  const renderSelectedNft = () => {
    if (!nft || !addressMatched) return

    const collection = nfts.find((c) => c.principal_id.toString() === nft.collection)

    return (
      <Stack spacing={2}>
        <Paper component={ListItem}>
          <ListItemAvatar>
            <Avatar sx={{ width: 35, height: 35 }} src={collection?.icon || nft?.url} />
          </ListItemAvatar>
          <ListItemText
            primary={collection?.name || 'Ordinal'}
            secondary={`#${nft.standard === 'ordinal' ? nft.number : nft.index}`}
          />
        </Paper>
        <NFTDisplayer nft={nft} />
      </Stack>
    )
  }

  const title = mode === SendMode.Send ? t('send.title') : t('send.canisterTopup')

  return (
    <>
      <Stack pt={2} pb={1} mx={1}>
        <Titlebar
          title={title}
          onActionClick={{
            type: 'back',
            action: () => navigate(-1),
          }}
        />
      </Stack>
      <Stack spacing={2} display="flex" direction="column" flex={1} px={2}>
        <AddressTextField
          isValidAddress={addressMatched}
          toAddress={toAddress}
          setToAddress={setToAddress}
          contacts={contactSections.map((s) => s.contacts).flat()}
          addressType={addressType}
          sendMode={mode}
        />

        {!addressMatched && (
          <ContactList
            contactSections={contactSections}
            onContactClick={({ address }) => setToAddress(address)}
          />
        )}

        {addressMatched && !hasSelectedAsset && (
          <Stack>
            <Typography variant="h6">Tokens</Typography>
            <Stack mx={-2.5}>
              <TokenList
                balances={balancesForSelect}
                onTokenClick={onTokenSelect}
                showPriceChange={false}
              />
            </Stack>

            <Stack mt={2} alignItems="center">
              <Typography fontSize={12} variant="subtitle1" textAlign="center">
                {t('send.noTokens')}
              </Typography>

              <Button
                variant="link"
                loading={isFindingTokenBalances}
                disabled={isFindingTokenBalances}
                size="small"
                onClick={async () => {
                  setIsFindingTokenBalances(true)
                  await findTokens(wallet!.principalId)
                  setIsFindingTokenBalances(false)
                }}
              >
                <Typography fontSize={13} textAlign="center">
                  {t('send.findTokens')}
                </Typography>
              </Button>
            </Stack>
          </Stack>
        )}

        {canSendNft && (
          <Stack>
            <Typography variant="h6" mb={1}>
              NFTs
            </Typography>
            <NftList nftChain={receiverChain} onNftClick={setNft} fromSend />
          </Stack>
        )}

        {addressMatched && token && (
          <TokenListItem
            balance={token}
            onClick={() => onTokenSelect(undefined)}
            showPriceChange={false}
          />
        )}

        {renderSelectedNft()}

        {addressMatched && token && (
          <Stack>
            <RainbowTextField
              value={amount}
              onChange={(e) => {
                sanitizeDecimalInput(e.target.value, setAmount)
              }}
              endAdornment={
                <Stack direction="row" alignItems="center">
                  {typeof maxAmount === 'number' && (
                    <Touchable scaleFactor={0.1}>
                      <Box
                        onClick={() => setAmount(maxAmount.toString())}
                        bgcolor={(theme) => theme.palette.background.default}
                        px={0.8}
                        py={0.4}
                        borderRadius={1}
                        mr={0.5}
                      >
                        <Typography fontSize={12}>MAX</Typography>
                      </Box>
                    </Touchable>
                  )}
                  <Typography>{token.token.symbol}</Typography>
                </Stack>
              }
              onFocused={() => setSelectedMode('token')}
              autoFocus
            />
          </Stack>
        )}

        {addressMatched && token?.tokenPrice && (
          <RainbowTextField
            value={amountCurrency}
            onChange={(e) => sanitizeDecimalInput(e.target.value, setAmountCurrency)}
            onFocused={() => setSelectedMode('currency')}
            endAdornment={userLocale.currency.symbol}
          />
        )}

        {showXTCSendMode && (
          <Picker
            values={[
              { key: 'burn', humanValue: '🔥 Burn' },
              { key: 'transfer', humanValue: '💸 Transfer' },
            ]}
            currentValue={xtcSendMode}
            onValueChange={setXtcSendMode}
            heading="Select Mode"
          />
        )}

        {utxosToUse.length > 0 && (
          <Box bgcolor={(theme) => theme.palette.secondary.dark}>
            <Accordion>
              <AccordionSummary
                expandIcon={<FaArrowDown color="white" />}
                aria-controls="utxo-content"
                id="utxo-header"
                sx={{
                  minHeight: 36,
                  pb: 0,
                }}
              >
                <Typography>{t('send.inputs')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1, px: 2 }}>
                <Box>
                  {utxosToUse.map((utxo, index) => {
                    const isOrdinal =
                      nft?.standard === 'ordinal' &&
                      nft?.txId === utxo.mintTxid &&
                      nft.mintIndex === utxo.mintIndex
                    return (
                      <Box key={index} display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 16,
                          }}
                        >
                          <FaArrowUp color="white" size={14} />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {(utxo.value / 100_000_000).toFixed(8)} BTC
                          </Typography>
                        </Box>
                        {isOrdinal && (
                          <Typography variant="caption" color="warning.main">
                            {t('send.selectedOrdinal')}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {ordinals.length > 0 && token?.token.chain === 'btc' && addressMatched && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={500}>
              {t('send.excludedOrdinals', {
                ordinals: ordinals.length,
              })}
            </Typography>
          </Alert>
        )}

        {!!cyclesOutAmount && (
          <Stack>
            <Typography variant="h5">{t('send.canisterReceives')}</Typography>
            <Typography>{formatNumber(cyclesOutAmount, 'TC')}</Typography>
          </Stack>
        )}

        <Box flex={1} />

        {fee && (fee.amount || 0) > 0 && addressMatched && hasSelectedAsset && (
          <Stack>
            <Typography>{t('send.fee')}</Typography>
            <Stack direction="row" spacing={1} alignContent="center">
              <Typography>{formatNumber(fee.amount, fee.token.symbol)}</Typography>
              {feeAmountCurrency && (
                <Typography color={(theme) => theme.palette.text.secondary}>
                  ({feeAmountCurrency})
                </Typography>
              )}
            </Stack>
          </Stack>
        )}

        <Stack pb={2}>
          {addressMatched && hasSelectedAsset && (
            <Button variant="rainbow" onClick={validateAndShowSummary} disabled={!!error} fullWidth>
              {error || title}
            </Button>
          )}
        </Stack>

        <BottomSheet visible={summaryVisible} onBackdropClick={() => setSummaryVisible(false)}>
          <SendReview
            nft={nft}
            toAddress={toAddress}
            token={token}
            amount={e8sAmount}
            cancelAction={() => setSummaryVisible(false)}
            onError={onError}
            xtcSendMode={xtcSendMode}
            utxos={utxosToUse}
            fee={fee}
            mode={mode}
            maxFeePerGas={maxFeePerGas}
            maxPriorityFeePerGas={maxPriorityFeePerGas}
            erc20TransferGas={erc20TransferGas}
            gasLimit={gasLimit}
          />
        </BottomSheet>
      </Stack>
      <ErrorPopup
        visible={!!sendError}
        title="Send Error"
        description={sendError}
        onDismiss={() => setSendError(undefined)}
      />
    </>
  )
}

export default Send
